import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "./LanguageContext";

const NAV_ITEMS = [
  { key: "home", target: "#top" },
  { key: "countdown", target: '[data-testid="countdown-section"]' },
  { key: "location", target: '[data-testid="location-section"]' },
  { key: "travel", target: '[data-testid="travel-section"]' },
  { key: "guestbook", target: '[data-testid="guestbook-section"]' },
  { key: "kina", target: '[data-testid="kina-section"]' },
];

function scrollToTarget(target) {
  const el =
    target === "#top"
      ? document.getElementById("top")
      : document.querySelector(target);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Header() {
  const { lang, setLanguage, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setMenuOpen(false);
    setTimeout(() => scrollToTarget(target), 80);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div
        className={`mx-auto flex items-center justify-between px-5 md:px-10 py-4 transition-colors duration-500 ${
          scrolled || menuOpen ? "bg-cream/85 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        {/* monogram */}
        <button
          onClick={() => go("#top")}
          data-testid="monogram"
          className="font-display text-xl md:text-2xl tracking-wide text-ink"
        >
          N <span className="text-gold">&amp;</span> E
        </button>

        {/* desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => go(item.target)}
              className="group relative font-sans text-xs uppercase tracking-[0.18em] text-ink/70 transition-colors duration-300 hover:text-turq"
            >
              {t.nav[item.key]}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* language toggle */}
          <div
            data-testid="language-toggle"
            className="flex items-center gap-1 rounded-full border border-gold/40 bg-cream/70 backdrop-blur-md p-1 shadow-[0_8px_32px_rgba(15,122,147,0.1)]"
          >
            {["nl", "tr"].map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => setLanguage(code)}
                className="relative px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.15em] rounded-full transition-colors duration-300"
              >
                {lang === code && (
                  <motion.span
                    layoutId="lang-pill"
                    className="absolute inset-0 rounded-full bg-turq"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <span
                  className={`relative z-10 ${
                    lang === code ? "text-white" : "text-ink/60"
                  }`}
                >
                  {code}
                </span>
              </button>
            ))}
          </div>

          {/* mobile hamburger */}
          <button
            data-testid="menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
            className="lg:hidden grid h-9 w-9 place-items-center rounded-full border border-gold/40 bg-cream/70 text-ink"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* mobile menu overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden mx-4 mb-2 rounded-sm border border-gold/30 bg-cream/95 backdrop-blur-md p-3 shadow-[0_20px_60px_rgba(10,31,40,0.18)]"
          >
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                data-testid={`mnav-${item.key}`}
                onClick={() => go(item.target)}
                className="block w-full text-left px-4 py-3 font-display text-2xl text-ink border-b border-ink/5 last:border-0 hover:text-turq transition-colors"
              >
                {t.nav[item.key]}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
